import { SkeletonBlock, SkeletonStack } from '../../../../widgets/skeleton';
import { ZTooltip } from '../../../../widgets/ztooltip';
import React, { FC, Suspense, useState } from 'react';
import { ChevronIcon } from '../../../../widgets/icons/scene';
import styled, { css } from 'styled-components';
import { T } from '../../../../../shared/helpers';

const Container = styled.div<{ $composer?: boolean }>`
    display: flex;
    flex-direction: row;
    height: 100%;
    max-height: 100%;
    overflow: hidden;

    ${(props) =>
        props.$composer &&
        css`
            background: var(--composer-panel, #fff);
            border: 1px solid var(--composer-line-soft, #eef1f3);
            border-radius: 16px;
            box-shadow: var(--composer-shadow, rgba(38, 50, 56, 0.04) 0 1px 2px);
        `}
`;

const MenuContainer = styled.div<{ $expanded?: boolean; $composer?: boolean; $side?: 'left' | 'right' }>`
    width: ${(props) => (props.$expanded ? '70px' : '43px')};
    flex: 0 0 ${(props) => (props.$expanded ? '70px' : '43px')};
    height: 100%;
    background-color: ${(props) => props.theme.app.pageBackgroundColor};
    padding-top: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    /* Il rail di destra è lo stesso rail, dall'altra parte: l'ordine nel flex lo mette dopo il
       contenuto, e il bordo cambia lato da solo. */
    order: ${(props) => (props.$side === 'right' ? 1 : 0)};

    ${(props) =>
        props.$composer &&
        css`
            width: ${props.$expanded ? '70px' : '43px'};
            flex: 0 0 ${props.$expanded ? '70px' : '43px'};
            background: var(--composer-sunken, #f9f9f9);
            border-radius: 16px 0 0 16px;
            padding: 10px 5px;
            gap: 2px;
        `}
`;

const LeftVerticalToolbarContainer = styled.div<{ $hiddenSidebar?: boolean; $collapsed?: boolean; $composer?: boolean }>`
    /* NASCOSTO VUOL DIRE NASCOSTO, e oggi lo vuol dire per caso: questo riquadro non dichiara un
       display, quindi vale quello del browser: [hidden] vuol dire display none. Il giorno che
       qualcuno gli desse un display — a un contenitore capita — l'attributo smetterebbe di
       valere IN SILENZIO, perché il foglio d'autore pesa quanto quello del browser e a parità
       vince chi arriva dopo. Questa riga non cambia niente adesso: serve a quel giorno lì. */
    &[hidden] {
        display: none;
    }

    flex: 1;
    min-width: 0;
    height: 100%;
    /* NIENTE PADDING E NIENTE SCORRIMENTO QUI: la sezione dentro porta i suoi margini e fa
       scorrere il suo contenuto. Con il padding qui, le righe divisorie non arrivavano ai bordi
       della colonna e la barra di scorrimento restava sedici pixel dentro; con l'overflow qui,
       le aree che scorrevano erano due, annidate, e quella di fuori non scorreva quasi mai. */
    padding: 0;
    background: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.backgroundColor};
    max-height: 100%;
    ${(props) =>
        (props.$hiddenSidebar || props.$collapsed) &&
        css`
            background: none;
        `}
    overflow: hidden;

    ${(props) =>
        props.$composer &&
        css`
            padding: 0;
            background: var(--composer-panel, #fff);

            /* The panel inside the rail card is the card: no frame of its own, or the column
               would draw a second line just inside its own border. */
            > section {
                border: 0;
                border-radius: 0;
                box-shadow: none;
            }
        `}
`;

// I glifi del prototipo sono a CONTORNO: non passano dal wrapper Icon condiviso,
// che impone `fill: currentColor` e `stroke: inherit` — riempirebbe le figure e
// cancellerebbe il tratto. Qui si dà solo la misura, il resto lo porta il glifo.
const RailGlyph = styled.span`
    display: inline-flex;

    svg {
        width: 20px;
        height: 20px;
        display: block;
    }

    /* IL COLORE DELLA VOCE DEVE ARRIVARE AL GLIFO. I glifi Material si riempiono — senza questa
       riga restano neri, e lo stato scelto si vedeva solo nell'etichetta; quelli disegnati a
       contorno tengono il loro tratto, che un riempimento cancellerebbe. */
    svg:not([stroke]) {
        fill: currentColor;
    }
`;

const MenuItemContainer = styled.button<{ $active?: boolean; $composer?: boolean; $expanded?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    margin: 0;
    border: 0;
    background: transparent;
    color: ${(props) => props.theme.app.textColor};
    padding: 12px 4px;

    &:hover {
        color: ${(props) => props.theme.app.secondary};
    }

    ${(props) =>
        props.$active &&
        css`
            color: ${props.theme.app.secondary};
        `}

    ${(props) =>
        props.$composer &&
        css`
            justify-content: center;
            gap: 3px;
            width: ${props.$expanded ? '60px' : '34px'};
            height: ${props.$expanded ? '60px' : '34px'};
            flex: 0 0 ${props.$expanded ? '60px' : '34px'};
            padding: 4px;
            border-radius: 8px;
            color: var(--composer-muted, #5c6b7a);

            /* Come il rail dello Scene Editor: la voce si accende di colore, senza pastiglia
               sotto. Il fondo grigio della barra fa già da sfondo, e una seconda superficie
               dentro l'altra dava due bordi a una voce sola. */
            &:hover {
                color: ${props.theme.app.secondary};
                background: transparent;
            }

            ${
                props.$active &&
                css`
                    color: ${props.theme.app.secondary};
                    background: transparent;
                `
            }
        `}
`;

const ToolLabel = styled.span<{ $composer?: boolean }>`
    margin-top: 4px;
    font-size: 10px;
    line-height: 1.1;
    text-align: center;

    ${(props) =>
        props.$composer &&
        css`
            display: block;
            width: 100%;
            overflow: hidden;
            margin-top: 0;
            font-size: 10px;
            font-weight: 600;
            line-height: 1.25;
            white-space: normal;
        `}
`;

// Il chevron punta verso il bordo che il pannello raggiungerebbe chiudendosi, e
// si gira quando è già chiuso: il comando per riaprire resta dove stava quello
// che ha chiuso, ed è l'unica chiusura che si sa annullare da sola.
//
// A DESTRA I DUE VERSI SI SCAMBIANO, perché si scambia il bordo: la colonna di destra si chiude
// verso destra. Il verso non è scritto due volte — è lo stesso conto letto allo specchio.
const ClosePanelButton = styled(MenuItemContainer)<{ $collapsed?: boolean; $side?: 'left' | 'right' }>`
    margin-top: auto;
    margin-bottom: 8px;

    svg {
        transform: rotate(${(props) => (props.$collapsed === (props.$side === 'right') ? '90deg' : '270deg')});
    }
`;

// I due separatori del rail dividono tre generi — le liste della scena, i
// contenuti della libreria, le proprietà dell'ambiente — e senza di loro nove
// icone in colonna sono un elenco senza capitoli.
const RailSeparator = styled.span`
    width: 22px;
    height: 1px;
    margin: 6px 0;
    background: ${(props) => props.theme.app.borderColor};
`;

const LoaderContainer = styled.div`
    width: 100%;
`;

const ToolbarPanelSkeleton = () => (
    <SkeletonStack aria-hidden='true' $gap='16px'>
        <SkeletonBlock $width='45%' $height='18px' />
        <SkeletonBlock $height='38px' $radius='8px' />
        <SkeletonBlock $height='38px' $radius='8px' />
        <SkeletonBlock $width='70%' $height='14px' $margin='8px 0 0' />
        <SkeletonBlock $height='96px' $radius='8px' />
    </SkeletonStack>
);

const TooltipV2 = styled(ZTooltip)`
    height: 20px;
    margin-left: 3px;
    svg {
        cursor: pointer;
        font-size: 20px;
        color: ${(props) => props.theme.app.secondary};
    }
`;

/**
 * UNA VOCE DEL RAIL: un glifo, una parola, e cosa fare quando la si preme. Non è una sezione —
 * chi la monta lo sa già — perché il rail di destra ha voci che sezioni non sono («Selected» è
 * ciò che stai guardando, non una lista).
 */
export interface SectionRailItem {
    key: string;
    icon: React.ReactNode;
    label: string;
    /** Quello che dice il tooltip; senza, lo dice l'etichetta. */
    tooltip?: string;
    /** Apre un genere nuovo: sopra la voce compare un separatore. */
    separatorBefore?: boolean;
}

export interface SectionRailProps {
    items: SectionRailItem[];
    activeKey?: string | null;
    onSelect?: (key: string) => void;
    /** Col rail esteso le voci portano anche la parola, non solo il glifo. */
    expanded?: boolean;
    /** Da che parte del pannello vive: cambia il posto nel flex e il verso del chevron. */
    side?: 'left' | 'right';
    collapsed?: boolean;
    onToggleCollapse?: () => void;
    variant?: 'default' | 'composer';
}

/**
 * IL RAIL È UNO SOLO, PER TUTT'E DUE I LATI. La colonna di destra aveva linguette orizzontali: una
 * riga in più da leggere prima della testata, e soprattutto un modo diverso di dire la stessa cosa
 * che dice la colonna di sinistra — dove sono, e come chiudo. Due lingue nella stessa schermata.
 *
 * Sta qui e non in un file suo perché qui vivono già le sue misure e i suoi stati: spostarle
 * vorrebbe dire riscriverle, ed è esattamente ciò che questa estrazione evita.
 */
export const SectionRail: FC<SectionRailProps> = ({
    items,
    activeKey,
    onSelect,
    expanded,
    side = 'left',
    collapsed,
    onToggleCollapse,
    variant = 'default'
}) => {
    const isComposer = variant === 'composer';
    const tooltipSide = side === 'right' ? 'left' : 'right';

    return (
        <MenuContainer $expanded={expanded} $composer={isComposer} $side={side}>
            {items.map((item, index) => (
                <React.Fragment key={item.key}>
                    {item.separatorBefore && index > 0 && <RailSeparator role='presentation' />}
                    <TooltipV2 title={item.tooltip ?? item.label} position={tooltipSide} addContainer>
                        <MenuItemContainer
                            $active={activeKey === item.key}
                            $composer={isComposer}
                            $expanded={expanded}
                            onClick={() => onSelect?.(item.key)}
                            aria-label={item.label}
                            aria-current={activeKey === item.key ? 'true' : undefined}
                        >
                            <RailGlyph>{item.icon}</RailGlyph>
                            {expanded && <ToolLabel $composer={isComposer}>{item.label}</ToolLabel>}
                        </MenuItemContainer>
                    </TooltipV2>
                </React.Fragment>
            ))}
            {onToggleCollapse && (
                <TooltipV2 title={collapsed ? T._('Open panel', 'Admin') : T._('Collapse panel', 'Admin')} position={tooltipSide} addContainer>
                    <ClosePanelButton
                        type='button'
                        $collapsed={collapsed}
                        $side={side}
                        aria-expanded={!collapsed}
                        aria-label={collapsed ? T._('Open panel', 'Admin') : T._('Collapse panel', 'Admin')}
                        onClick={onToggleCollapse}
                    >
                        <RailGlyph>
                            <ChevronIcon />
                        </RailGlyph>
                    </ClosePanelButton>
                </TooltipV2>
            )}
        </MenuContainer>
    );
};

export interface StepsTools {
    tool: string;
    icon: React.ReactNode;
    tooltip: string;
    onClick: () => void;
    component: React.FC<any>;
    /** Apre un genere nuovo nel rail: sopra la voce compare un separatore. */
    separatorBefore?: boolean;
}

export type LeftVerticalToolbarProps = {
    tools: StepsTools[];
    hiddenSidebar?: boolean;
    selectedTool?: string | null;
    onSelectTool?: (tool: string) => void;
    onClosePanel?: () => void;
    collapsed?: boolean;
    sectionRails?: 'compact' | 'expanded';
    onInspectorChange?: (component: React.ReactNode | null) => void;
    variant?: 'default' | 'composer';
    /**
     * Le sezioni che **non** monta il rail: il loro nodo vive altrove e viene traslocato qui
     * dentro quando tocca a questo attracco. Il rail offre il posto — `contentRef` — e non
     * le costruisce, perché ricostruirle a ogni cambio di attracco vorrebbe dire perderne lo
     * stato (docs/scene-editor-step6-second-layout.md § 1).
     */
    hostedTools?: string[];
    /** Il contenitore in cui il rail mostra la sezione attiva. */
    contentRef?: React.Ref<HTMLDivElement>;
};
const LeftVerticalToolbar: FC<LeftVerticalToolbarProps> = ({
    tools,
    hiddenSidebar,
    selectedTool,
    onSelectTool,
    onClosePanel,
    collapsed,
    sectionRails = 'compact',
    onInspectorChange,
    hostedTools,
    contentRef,
    variant = 'default'
}) => {
    const [internalView, setInternalView] = useState<string | undefined>(tools[0]?.tool);
    const view = selectedTool ?? internalView;
    const isComposer = variant === 'composer';

    return (
        <Container $composer={isComposer}>
            <SectionRail
                items={tools.map((stepTool) => ({
                    key: stepTool.tool,
                    icon: stepTool.icon,
                    label: stepTool.tool,
                    tooltip: stepTool.tooltip,
                    separatorBefore: stepTool.separatorBefore
                }))}
                activeKey={view}
                onSelect={(key) => {
                    setInternalView(key);
                    onSelectTool?.(key);
                    tools.find((stepTool) => stepTool.tool === key)?.onClick();
                }}
                expanded={sectionRails === 'expanded'}
                side='left'
                collapsed={collapsed}
                onToggleCollapse={onClosePanel}
                variant={variant}
            />
            {/* Chiuso, del pannello resta la sola barra: il contenuto non si ritaglia, esce. */}
            <LeftVerticalToolbarContainer ref={contentRef} $hiddenSidebar={hiddenSidebar} $collapsed={collapsed} $composer={isComposer} hidden={collapsed}>
                <Suspense
                    fallback={
                        <LoaderContainer>
                            <ToolbarPanelSkeleton />
                        </LoaderContainer>
                    }
                >
                    {tools.map((stepTool) => {
                        if (view !== stepTool.tool) return null;
                        if (hostedTools?.includes(stepTool.tool)) return null;
                        const ToolComponent = stepTool.component;
                        return <ToolComponent key={stepTool.tool} onInspectorChange={onInspectorChange} />;
                    })}
                </Suspense>
            </LeftVerticalToolbarContainer>
        </Container>
    );
};

export default LeftVerticalToolbar;
